import { MarginCalculator } from './MarginCalculator'
import { OperationalCostCalculator } from './OperationalCostCalculator'
import { ProfitCalculator } from './ProfitCalculator'
import { TaxCalculator } from './TaxCalculator'
import type {
  PriceAlert,
  PriceLineInput,
  PriceLineResult,
  PriceSummary,
  PricingRates,
  RecalcTrigger,
} from './types'
import { clampPercent, round2, safeNumber } from './math'

/**
 * Orchestrates line-level pricing rules.
 * Never import React here — pure domain logic.
 */
export class PriceCalculator {
  private readonly operational = new OperationalCostCalculator()
  private readonly tax = new TaxCalculator()
  private readonly profit = new ProfitCalculator()
  private readonly margin = new MarginCalculator()

  applyRates(purchasePrice: number, quantity: number, rates: PricingRates): PriceLineResult {
    const purchase = Math.max(0, safeNumber(purchasePrice))
    const qty = Math.max(1, Math.floor(safeNumber(quantity, 1)))
    const other = round2(purchase * (clampPercent(rates.otherCostsPercent) / 100))
    const operational = this.operational.calculate(purchase, rates.operationalPercent)
    const taxable = purchase + operational + other
    const taxes = this.tax.calculate(taxable, rates.taxPercent)
    const costBase = taxable + taxes
    const profit = this.profit.calculate(costBase, rates.profitPercent)
    const salePrice = round2(purchase + operational + other + taxes + profit)

    return this.toResult({
      id: 'tmp',
      quantity: qty,
      purchasePrice: purchase,
      operational: round2(operational + other),
      taxes,
      profit,
      salePrice,
    })
  }

  recalculate(input: PriceLineInput, rates: PricingRates, trigger: RecalcTrigger): PriceLineResult {
    const quantity = Math.max(1, Math.floor(safeNumber(input.quantity, 1)))
    const purchase = Math.max(0, safeNumber(input.purchasePrice))

    if (trigger === 'sale') {
      const salePrice = Math.max(0, safeNumber(input.salePrice))
      const operational =
        input.operational != null
          ? this.operational.fromAbsolute(input.operational)
          : this.operational.calculate(purchase, rates.operationalPercent)
      const taxes =
        input.taxes != null
          ? this.tax.fromAbsolute(input.taxes)
          : this.tax.calculate(purchase + operational, rates.taxPercent)
      const profit = this.profit.fromSale(salePrice, purchase, operational, taxes)
      return this.toResult({
        id: input.id,
        quantity,
        purchasePrice: purchase,
        operational,
        taxes,
        profit,
        salePrice,
      })
    }

    if (trigger === 'purchase') {
      return {
        ...this.applyRates(purchase, quantity, rates),
        id: input.id,
      }
    }

    if (trigger === 'profit') {
      const operational =
        input.operational != null
          ? this.operational.fromAbsolute(input.operational)
          : this.operational.calculate(purchase, rates.operationalPercent)
      const taxes =
        input.taxes != null
          ? this.tax.fromAbsolute(input.taxes)
          : this.tax.calculate(purchase + operational, rates.taxPercent)
      const profit = this.profit.fromAbsolute(input.profit ?? 0)
      const salePrice = round2(purchase + operational + taxes + profit)
      return this.toResult({
        id: input.id,
        quantity,
        purchasePrice: purchase,
        operational,
        taxes,
        profit,
        salePrice,
      })
    }

    // components: honor absolute operational/taxes/profit when provided
    const operational =
      input.operational != null
        ? this.operational.fromAbsolute(input.operational)
        : this.operational.calculate(purchase, rates.operationalPercent)
    const taxes =
      input.taxes != null
        ? this.tax.fromAbsolute(input.taxes)
        : this.tax.calculate(purchase + operational, rates.taxPercent)
    const profit =
      input.profit != null
        ? this.profit.fromAbsolute(input.profit)
        : this.profit.calculate(purchase + operational + taxes, rates.profitPercent)
    const salePrice = round2(purchase + operational + taxes + profit)

    return this.toResult({
      id: input.id,
      quantity,
      purchasePrice: purchase,
      operational,
      taxes,
      profit,
      salePrice,
    })
  }

  summarize(
    lines: PriceLineResult[],
    discountPercent: number,
    discountValue: number,
    freightAbsolute: number,
    freightPercent: number,
  ): PriceSummary {
    const purchaseTotal = round2(lines.reduce((s, l) => s + l.purchasePrice * l.quantity, 0))
    const operationalTotal = round2(lines.reduce((s, l) => s + l.operational * l.quantity, 0))
    const taxesTotal = round2(lines.reduce((s, l) => s + l.taxes * l.quantity, 0))
    const profitGross = round2(lines.reduce((s, l) => s + l.profit * l.quantity, 0))
    const saleTotal = round2(lines.reduce((s, l) => s + l.subtotal, 0))

    const percentDiscount = round2(saleTotal * (clampPercent(discountPercent) / 100))
    const discount = round2(percentDiscount + Math.max(0, discountValue))
    const freightFromPercent = round2(saleTotal * (clampPercent(freightPercent) / 100))
    const freight = round2(Math.max(0, freightAbsolute) + freightFromPercent)
    const finalPrice = round2(Math.max(0, saleTotal - discount + freight))
    const profitNet = round2(profitGross - discount)
    const costTotal = round2(purchaseTotal + operationalTotal + taxesTotal)

    return {
      purchaseTotal,
      operationalTotal,
      taxesTotal,
      profitGross,
      profitNet,
      saleTotal,
      discount,
      freight,
      finalPrice,
      marginPercent: this.margin.marginPercent(profitNet, finalPrice),
      markupPercent: this.margin.markupPercent(profitNet, purchaseTotal),
      roiPercent: this.margin.roiPercent(profitNet, costTotal),
      profitPercent: purchaseTotal > 0 ? round2((profitNet / purchaseTotal) * 100) : 0,
    }
  }

  detectAlerts(summary: PriceSummary): PriceAlert[] {
    const alerts: PriceAlert[] = []
    if (summary.marginPercent < 10) {
      alerts.push({
        code: 'MARGIN_BELOW_10',
        severity: 'warning',
        message: 'Margem abaixo de 10%',
      })
    }
    if (summary.profitNet < 0) {
      alerts.push({
        code: 'NEGATIVE_PROFIT',
        severity: 'danger',
        message: 'Lucro negativo',
      })
    }
    if (summary.finalPrice < summary.purchaseTotal) {
      alerts.push({
        code: 'SALE_BELOW_COST',
        severity: 'danger',
        message: 'Venda abaixo do custo',
      })
    }
    return alerts
  }

  private toResult(input: {
    id: string
    quantity: number
    purchasePrice: number
    operational: number
    taxes: number
    profit: number
    salePrice: number
  }): PriceLineResult {
    const costTotal = round2(
      (input.purchasePrice + input.operational + input.taxes) * input.quantity,
    )
    return {
      id: input.id,
      quantity: input.quantity,
      purchasePrice: round2(input.purchasePrice),
      operational: round2(input.operational),
      taxes: round2(input.taxes),
      profit: round2(input.profit),
      salePrice: round2(input.salePrice),
      subtotal: round2(input.salePrice * input.quantity),
      marginPercent: this.margin.marginPercent(input.profit, input.salePrice),
      markupPercent: this.margin.markupPercent(input.profit, input.purchasePrice),
      costTotal,
    }
  }
}
